import { storage } from "@/lib/storage";
import { MapItemSUP } from "@/types/search";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// Optimized constants to prevent recreating objects
const EMPTY_FAVORITES: MapItemSUP[] = [];
const EMPTY_SELECTED_MARKERS: Record<string, MapItemSUP> = Object.create(null);

export interface InteractionState {
	hoveredMarkerId: string | null;
	selectedMarkers: Record<string, MapItemSUP>;
	selectedCardId: string | null;
	favorites: MapItemSUP[];
	viewedProperties: Set<string>;
	view: "map" | "list";
	currentCarouselIndex: number;
	// isComparisonMinimized and its setter are removed.

	// methods
	setHoveredMarkerId: (id: string | null) => void;
	setSelectedMarkers: (markers: Record<string, MapItemSUP>) => void;
	addSelectedMarker: (id: string, data: MapItemSUP) => void;
	removeSelectedMarker: (id: string) => void;
	toggleSelectedMarkers: (id: string, data: MapItemSUP) => void;
	clearSelectedMarkers: () => void;
	setSelectedCardId: (id: string | null) => void;
	initFavorites: (favorites: MapItemSUP[]) => void;
	setFavorites: (favorites: MapItemSUP[]) => void;
	toggleFavorite: (property: MapItemSUP) => void;
	setView: (view: "map" | "list") => void;
	setCurrentCarouselIndex: (index: number) => void;
}

export function createInteractionStore({
	initialHoveredMarkerId = null,
	initialSelectedMarkers = EMPTY_SELECTED_MARKERS,
	initialSelectedCardId = null,
	initialFavorites = EMPTY_FAVORITES,
	initialView = "map",
}: {
	initialHoveredMarkerId?: string | null;
	initialSelectedMarkers?: Record<string, MapItemSUP>;
	initialSelectedCardId?: string | null;
	initialFavorites?: MapItemSUP[];
	initialView?: "map" | "list";
} = {}) {
	return create<InteractionState>()(
		devtools(
			subscribeWithSelector(
				immer(set => ({
					hoveredMarkerId: initialHoveredMarkerId,
					selectedMarkers: initialSelectedMarkers,
					selectedCardId: initialSelectedCardId,
					favorites: initialFavorites,
					viewedProperties: new Set<string>(),
					view: initialView,
					currentCarouselIndex: 0,
					setHoveredMarkerId: id =>
						set(state => {
							state.hoveredMarkerId = id;
						}),
					setSelectedMarkers: markers =>
						set(state => {
							// Only update if actually changed to prevent unnecessary re-renders
							if (state.selectedMarkers !== markers) {
								state.selectedMarkers = markers;
							}
						}),
					addSelectedMarker: (id, data) =>
						set(state => {
							state.selectedMarkers[id] = data;
						}),
					removeSelectedMarker: id =>
						set(state => {
							delete state.selectedMarkers[id];
						}),
					toggleSelectedMarkers: (id, data) =>
						set(state => {
							if (id in state.selectedMarkers) {
								delete state.selectedMarkers[id];
							} else {
								state.selectedMarkers[id] = data;
							}
						}),
					clearSelectedMarkers: () =>
						set(state => {
							// Only clear if there are markers to clear
							if (Object.keys(state.selectedMarkers).length > 0) {
								state.selectedMarkers = Object.create(null);
							}
						}),
					setSelectedCardId: id =>
						set(state => {
							state.selectedCardId = id;
						}),
					initFavorites: favorites =>
						set(state => {
							// Optimized comparison to avoid JSON.stringify overhead
							if (
								state.favorites.length === favorites.length &&
								state.favorites.every((fav, index) => fav._id === favorites[index]?._id)
							) {
								return;
							}
							state.favorites = favorites;
						}),
					setFavorites: favorites =>
						set(state => {
							// Optimized comparison to avoid JSON.stringify overhead
							if (
								state.favorites.length === favorites.length &&
								state.favorites.every((fav, index) => fav._id === favorites[index]?._id)
							) {
								return;
							}
							state.favorites = favorites;

							// Optimized storage write with error handling (don't await inside set)
							if (typeof window !== "undefined") {
								storage.setItem("tray_properties", favorites).catch(error => {
									// eslint-disable-next-line no-console
									console.warn("Failed to save favorites to storage:", error);
								});
							}
						}),
					toggleFavorite: property =>
						set(state => {
							const idx = state.favorites.findIndex(fav => fav._id === property._id);
							if (idx !== -1) {
								state.favorites.splice(idx, 1);
							} else {
								state.favorites.push(property);
							}

							// Update storage immediately for favorites (don't await inside set)
							if (typeof window !== "undefined") {
								storage.setItem("tray_properties", state.favorites).catch(error => {
									// eslint-disable-next-line no-console
									console.warn("Failed to save favorites to storage:", error);
								});
							}
						}),
					setView: view =>
						set(state => {
							state.view = view;
						}),
					setCurrentCarouselIndex: index =>
						set(state => {
							state.currentCarouselIndex = index;
						}),
				})),
			),
			{ name: "searchPage-interaction-store" },
		),
	);
}
